<template>
  <Teleport to="body">
    <div
      v-if="open || keepContent"
      v-show="(keepContent && open) || !keepContent"
      ref="modalEl"
      v-bind="$attrs"
      class="modal__wrapper"
      @click="outside($event)"
    >
      <div
        ref="modalBoxEl"
        class="modal__box"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="labelledBy"
        :aria-label="labelledBy ? null : ariaLabel || null"
        :class="{
          'modal__box--full-height': fullHeight,
          'modal__box--full-max-height': !fullHeight && contentScrollable,
          'modal__box--with-sidebar': sidebar,
          'modal__box--full-screen': fullScreen,
          'modal__box--wide': wide,
          'modal__box--small': small,
          'modal__box--tiny': tiny,
          'modal__box--right': right,
          'modal__box--no-padding': !boxPadding,
        }"
      >
        <template v-if="sidebar">
          <div
            v-if="leftSidebar"
            class="modal__box-sidebar modal__box-sidebar--left"
            :class="{ 'modal__box-sidebar--scrollable': leftSidebarScrollable }"
          >
            <slot name="sidebar"></slot>
          </div>
          <div
            class="modal__box-content"
            :class="{
              'modal__box-content--scrollable': contentScrollable,
              'modal__box-content-no-padding': !contentPadding,
            }"
          >
            <slot name="content"></slot>
            <div class="modal__actions">
              <button
                v-if="closeButton && canClose"
                type="button"
                :title="$t('action.close')"
                :aria-label="$t('action.close')"
                class="modal__close"
                @click="hide()"
              >
                <i class="iconoir-cancel"></i>
              </button>

              <button
                v-if="collapsibleRightSidebar"
                type="button"
                class="modal__collapse"
                :title="
                  sidebarCollapsed
                    ? $t('action.expandSidebar')
                    : $t('action.collapseSidebar')
                "
                :aria-label="
                  sidebarCollapsed
                    ? $t('action.expandSidebar')
                    : $t('action.collapseSidebar')
                "
                :aria-expanded="!sidebarCollapsed"
                @click="collapseSidebar"
              >
                <i
                  :class="{
                    'iconoir-fast-arrow-right': !sidebarCollapsed,
                    'iconoir-fast-arrow-left': sidebarCollapsed,
                  }"
                ></i>
              </button>
              <slot name="actions"></slot>
            </div>
          </div>

          <div
            v-if="rightSidebar"
            class="modal__box-sidebar modal__box-sidebar--right"
            :class="{
              'modal__box-sidebar--scrollable': rightSidebarScrollable,
              'modal__box-sidebar--collapsed': sidebarCollapsed,
            }"
          >
            <slot v-if="!sidebarCollapsed" name="sidebar"></slot>
          </div>
        </template>
        <template v-if="!sidebar">
          <slot></slot>
          <slot name="content"></slot>
          <div class="modal__actions">
            <button
              v-if="closeButton && canClose"
              type="button"
              :title="$t('action.close')"
              :aria-label="$t('action.close')"
              class="modal__close"
              @click="hide()"
            >
              <i class="iconoir-cancel"></i>
            </button>
            <slot name="actions"></slot>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script>
import baseModal from '@baserow/modules/core/mixins/baseModal'
import { getElementFromRef } from '@baserow/modules/core/utils/dom'
import {
  findLabelId,
  focusFirstElement,
  handleDialogTabKey,
  restoreFocus,
} from '@baserow/modules/core/utils/dialogFocus'

export default {
  name: 'Modal',
  mixins: [baseModal],
  inheritAttrs: false,
  props: {
    /**
     * Accessible name for the dialog, used only when the modal renders no
     * heading of its own. Most call sites do render one (an `<h2
     * class="box__title">` in the great majority), and that heading is picked
     * up automatically, so this is the escape hatch rather than the norm --
     * see `findLabelId` for why detection is preferred over a required prop.
     */
    ariaLabel: {
      type: String,
      default: null,
      required: false,
    },
    leftSidebar: {
      type: Boolean,
      default: false,
      required: false,
    },
    rightSidebar: {
      type: Boolean,
      default: false,
      required: false,
    },
    fullScreen: {
      type: Boolean,
      default: false,
      required: false,
    },
    wide: {
      type: Boolean,
      default: false,
      required: false,
    },
    small: {
      type: Boolean,
      default: false,
      required: false,
    },
    tiny: {
      type: Boolean,
      default: false,
      required: false,
    },
    closeButton: {
      type: Boolean,
      default: true,
      required: false,
    },
    fullHeight: {
      type: Boolean,
      default: false,
      required: false,
    },
    leftSidebarScrollable: {
      type: Boolean,
      default: false,
      required: false,
    },
    contentScrollable: {
      type: Boolean,
      default: false,
      required: false,
    },
    contentPadding: {
      type: Boolean,
      default: true,
      required: false,
    },
    rightSidebarScrollable: {
      type: Boolean,
      default: false,
      required: false,
    },
    collapsibleRightSidebar: {
      type: Boolean,
      default: false,
      required: false,
    },
    boxPadding: {
      type: Boolean,
      default: true,
      required: false,
    },
    right: {
      type: Boolean,
      default: false,
      required: false,
    },
    // This flag allow to keep the modal content in case you want it to be available.
    // Useful if you have a form inside the modal that is a sub part of the current
    // form.
    keepContent: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      sidebarCollapsed: false,
      // Id of the heading naming this dialog. Resolved from the rendered
      // content on open rather than declared, and re-resolved on update
      // because many modals title themselves from data that arrives after the
      // modal is already on screen.
      labelledBy: null,
    }
  },
  computed: {
    sidebar() {
      return this.leftSidebar || this.rightSidebar
    },
  },
  updated() {
    // Modal contents are not static: titles arrive with an async fetch, steps
    // of a wizard swap the heading, and `v-if`-ed sections appear. Re-reading
    // on update keeps `aria-labelledby` pointed at whatever actually names the
    // dialog now, and repairs the case where the first attempt ran before the
    // heading existed.
    if (this.open) {
      this._applyDialogLabel()
    }
  },
  beforeUnmount() {
    // A modal can be torn down while open -- its route changes, or the row it
    // belongs to is deleted. `baseModal.hide()` is not reached in that case,
    // so the Tab listener has to come off here as well.
    this._cleanupDialogEventHandlers()
  },
  methods: {
    collapseSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },
    /**
     * Opens the modal and moves focus into it.
     *
     * Escape is deliberately not handled here: `baseModal.js` already binds it
     * on `keyup` and already refuses to close while `children` holds an open
     * modal or context, which is what makes a dropdown inside a modal close on
     * the first Escape and the modal only on the second. Adding a second
     * handler would close both on one press.
     */
    async show(...args) {
      // Remembered before the dialog takes focus, so it can be handed back on
      // close. Without it, closing a modal drops focus onto `<body>` and a
      // keyboard user has to Tab from the top of the document to get back to
      // the control they opened it from.
      this._returnFocusTo =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null

      baseModal.methods.show.apply(this, args)

      // The box only exists after the `v-if` has rendered.
      await this.$nextTick()
      this._applyDialogLabel()
      this._moveFocusIn()

      this._cleanupDialogEventHandlers()

      // Bound on the box rather than the document so a nested modal's trap
      // takes the event first and the outer one never sees it -- the inner
      // dialog is the one the user is in.
      this._onDialogKeyDown = (event) => {
        if (!this.open) {
          return
        }
        handleDialogTabKey(event, this.getDialogElement())
      }
      const boxEl = this.getDialogElement()
      if (boxEl) {
        boxEl.addEventListener('keydown', this._onDialogKeyDown)
      }
    },
    /**
     * Closes the modal and hands focus back to whatever opened it.
     */
    hide(emit = true) {
      const wasOpen = this.open
      const returnFocusTo = this._returnFocusTo
      const boxEl = this.getDialogElement()

      baseModal.methods.hide.call(this, emit)

      // `baseModal.hide()` returns early without closing when a child modal or
      // context is still open. Focus must stay where it is in that case, and
      // the opener must stay remembered for the eventual real close.
      if (!wasOpen || this.children.some((child) => child.open === true)) {
        return
      }

      this._returnFocusTo = null
      this._cleanupDialogEventHandlers()
      restoreFocus(boxEl, returnFocusTo)
    },
    /**
     * Moves focus to the first focusable control, or the dialog box itself
     * when there is none yet.
     *
     * Retried on the next frame when the box is still empty. Several modals
     * render their body from an async fetch -- the template modal and the row
     * edit modal both do -- and focusing before that content exists would
     * leave focus on the container while the Tab order behind the dialog stays
     * live.
     */
    _moveFocusIn() {
      const boxEl = this.getDialogElement()
      if (!boxEl) {
        return
      }
      const focused = focusFirstElement(boxEl)
      if (focused === boxEl) {
        this.$nextTick(() => {
          // Only if nothing has taken focus in the meantime -- an autofocused
          // input inside the modal is a better target than anything chosen
          // here, and must not be stolen from.
          if (this.open && document.activeElement === boxEl) {
            focusFirstElement(boxEl)
          }
        })
      }
    },
    _applyDialogLabel() {
      const labelId = findLabelId(this.getDialogElement())
      if (labelId !== this.labelledBy) {
        this.labelledBy = labelId
      }
    },
    _cleanupDialogEventHandlers() {
      if (this._onDialogKeyDown) {
        const boxEl = this.getDialogElement()
        if (boxEl) {
          boxEl.removeEventListener('keydown', this._onDialogKeyDown)
        }
        this._onDialogKeyDown = null
      }
    },
    /**
     * The `.modal__box` element, which is the node carrying `role="dialog"`.
     *
     * Not the same as `getTeleportedElement()`, which returns the full-screen
     * `.modal__wrapper` backdrop -- trapping focus against the wrapper would
     * be wrong, since the backdrop is not part of the dialog.
     *
     * @returns {HTMLElement|null}
     */
    getDialogElement() {
      const el = getElementFromRef(this.$refs.modalBoxEl)
      return el instanceof HTMLElement ? el : null
    },
  },
}
</script>
